import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import type { FileItem, FileVersion, PaginatedResponse } from '@/types/api';

export function useFiles(folderId: string | null | undefined, includeDeleted = false) {
  return useQuery({
    enabled: !!folderId,
    queryKey: ['files', folderId, { includeDeleted }],
    queryFn: async () =>
      (await api.get<FileItem[]>(`/folders/${folderId}/files`, {
        params: includeDeleted ? { include_deleted: true } : undefined,
      })).data,
  });
}

export function useFile(fileId: string | undefined) {
  return useQuery({
    enabled: !!fileId,
    queryKey: ['files', 'detail', fileId],
    queryFn: async () => (await api.get<FileItem>(`/files/${fileId}`)).data,
  });
}

export function useFileVersions(fileId: string | undefined) {
  return useQuery({
    enabled: !!fileId,
    queryKey: ['files', 'versions', fileId],
    queryFn: async () => (await api.get<FileVersion[]>(`/files/${fileId}/versions`)).data,
  });
}

export function useRenameFile() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (vars: { id: string; name: string }) => {
      const { data } = await api.patch<FileItem>(`/files/${vars.id}/rename`, { name: vars.name });
      return data;
    },
    onSuccess: (d) => {
      qc.invalidateQueries({ queryKey: ['files', d.folder_id] });
      qc.invalidateQueries({ queryKey: ['files', 'detail', d.id] });
    },
  });
}

export function useMoveFile() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (vars: { id: string; folder_id: string; prev_folder_id: string }) => {
      const { data } = await api.patch<FileItem>(`/files/${vars.id}/move`, { folder_id: vars.folder_id });
      return data;
    },
    onSuccess: (d, vars) => {
      qc.invalidateQueries({ queryKey: ['files', vars.prev_folder_id] });
      qc.invalidateQueries({ queryKey: ['files', d.folder_id] });
      qc.invalidateQueries({ queryKey: ['files', 'detail', d.id] });
    },
  });
}

export function useSoftDeleteFile() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (vars: { id: string; folder_id: string }) => {
      await api.delete(`/files/${vars.id}`);
    },
    onSuccess: (_d, vars) => qc.invalidateQueries({ queryKey: ['files', vars.folder_id] }),
  });
}

export function useRestoreFile() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (vars: { id: string; folder_id: string }) => {
      await api.post(`/files/${vars.id}/restore`);
    },
    onSuccess: (_d, vars) => qc.invalidateQueries({ queryKey: ['files', vars.folder_id] }),
  });
}

export interface UploadProgress {
  fileName: string;
  loaded: number;
  total: number;
  percent: number;
  status: 'pending' | 'uploading' | 'done' | 'error';
  error?: string;
}

/**
 * Upload a single file via presigned PUT directly to R2.
 * Phase 1: get presigned URL from API.
 * Phase 2: PUT file bytes directly to R2 (progress events work normally).
 * Phase 3: tell API to create the DB record.
 */
export async function uploadFile(
  folderId: string,
  file: File,
  onProgress?: (p: UploadProgress) => void,
): Promise<FileItem> {
  // Phase 1 — get presigned PUT URL
  const { data: urlData } = await api.post<{
    upload_url: string;
    storage_key: string;
    expires_at: string;
  }>(`/folders/${folderId}/upload-url`, {
    filename: file.name,
    content_type: file.type || 'application/octet-stream',
  });

  // Phase 2 — PUT directly to R2 with progress tracking
  await new Promise<void>((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('PUT', urlData.upload_url, true);
    xhr.setRequestHeader('Content-Type', file.type || 'application/octet-stream');

    xhr.upload.onprogress = (ev) => {
      if (!onProgress) return;
      const total = ev.lengthComputable ? ev.total : file.size;
      onProgress({
        fileName: file.name,
        loaded: ev.loaded,
        total,
        percent: total ? Math.round((ev.loaded / total) * 100) : 0,
        status: 'uploading',
      });
    };

    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        onProgress?.({ fileName: file.name, loaded: file.size, total: file.size, percent: 100, status: 'uploading' });
        resolve();
      } else {
        const msg = `Upload failed (${xhr.status})`;
        onProgress?.({ fileName: file.name, loaded: 0, total: file.size, percent: 0, status: 'error', error: msg });
        reject(new Error(msg));
      }
    };

    xhr.onerror = () => {
      const msg = 'Network error during upload';
      onProgress?.({ fileName: file.name, loaded: 0, total: file.size, percent: 0, status: 'error', error: msg });
      reject(new Error(msg));
    };

    xhr.send(file);
  });

  // Phase 3 — finalize (create DB record)
  const { data: fileItem } = await api.post<FileItem>(`/folders/${folderId}/complete-upload`, {
    storage_key: urlData.storage_key,
    filename: file.name,
    content_type: file.type || 'application/octet-stream',
    size_bytes: file.size,
  });

  onProgress?.({
    fileName: file.name,
    loaded: file.size,
    total: file.size,
    percent: 100,
    status: 'done',
  });

  return fileItem;
}

export function downloadFileUrl(_fileId: string): string {
  // Kept for backwards compatibility — use downloadFile() for actual downloads
  return `/api/files/${_fileId}/download`;
}

interface PreviewUrlResponse {
  url: string;
  expires_at: string;
}

/**
 * Fetch a presigned R2 URL for inline preview. Cached for 55 minutes — expiry is 1 hour.
 */
export function useFilePreviewUrl(fileId: string | null | undefined) {
  return useQuery({
    enabled: !!fileId,
    queryKey: ['files', 'preview-url', fileId],
    queryFn: async () =>
      (await api.post<PreviewUrlResponse>(`/files/${fileId}/preview-url`)).data,
    staleTime: 55 * 60 * 1000,
    gcTime: 60 * 60 * 1000,
    refetchOnWindowFocus: false,
  });
}

export async function downloadFile(fileId: string, filename: string): Promise<void> {
  const { data } = await api.get<{ url: string; filename: string }>(`/files/${fileId}/download-url`);
  const a = document.createElement('a');
  a.href = data.url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
}

export interface SearchParams {
  q?: string;
  org_id?: string;
  folder_id?: string;
  uploaded_by_id?: string;
  from_date?: string;
  to_date?: string;
  limit?: number;
  offset?: number;
}

export interface SearchResultFile {
  id: string;
  name: string;
  folder_id: string;
  folder_name?: string | null;
  organization_id?: string;
  uploaded_by_id?: string | null;
  created_at?: string | null;
  is_deleted: boolean;
}

export function useSearchFiles(params: SearchParams) {
  const limit = params.limit ?? 50;
  const offset = params.offset ?? 0;
  return useQuery({
    enabled:
      !!params.q ||
      !!params.org_id ||
      !!params.folder_id ||
      !!params.uploaded_by_id,
    queryKey: ['files', 'search', { ...params, limit, offset }],
    queryFn: async () =>
      (
        await api.get<PaginatedResponse<SearchResultFile>>('/files/search', {
          params: { ...params, limit, offset },
        })
      ).data,
  });
}

/**
 * Streams a ZIP archive from the backend containing the requested files
 * (or all non-deleted files in the folder when fileIds is empty) and
 * triggers a browser download named `{folderName}.zip`.
 */
export async function downloadFolderZip(
  folderId: string,
  fileIds: string[],
  folderName: string,
): Promise<void> {
  const resp = await api.post<Blob>(
    `/folders/${folderId}/download-zip`,
    { file_ids: fileIds },
    { responseType: 'blob' },
  );
  const url = URL.createObjectURL(resp.data);
  const a = document.createElement('a');
  a.href = url;
  const safeName = (folderName || 'files').replace(/[^A-Za-z0-9._\- ]+/g, '_').trim() || 'files';
  a.download = `${safeName}.zip`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

export interface BulkDeleteResult {
  deleted: number;
  failed: number;
}

export interface BulkMoveResult {
  moved: number;
  failed: number;
}

export function useBulkDeleteFiles() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (vars: { file_ids: string[]; folder_id?: string }) => {
      const { data } = await api.post<BulkDeleteResult>('/files/bulk-delete', {
        file_ids: vars.file_ids,
      });
      return data;
    },
    onSuccess: (_d, vars) => {
      if (vars.folder_id) {
        qc.invalidateQueries({ queryKey: ['files', vars.folder_id] });
      } else {
        qc.invalidateQueries({ queryKey: ['files'] });
      }
    },
  });
}

export function useBulkMoveFiles() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (vars: {
      file_ids: string[];
      folder_id: string;
      prev_folder_id?: string;
    }) => {
      const { data } = await api.post<BulkMoveResult>('/files/bulk-move', {
        file_ids: vars.file_ids,
        folder_id: vars.folder_id,
      });
      return data;
    },
    onSuccess: (_d, vars) => {
      if (vars.prev_folder_id) {
        qc.invalidateQueries({ queryKey: ['files', vars.prev_folder_id] });
      }
      qc.invalidateQueries({ queryKey: ['files', vars.folder_id] });
    },
  });
}

/**
 * Restores many files in parallel by calling the per-file restore endpoint.
 * Used by the bulk-delete toast's Undo action.
 */
export function useBulkRestoreFiles() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (vars: { file_ids: string[]; folder_id: string }) => {
      const results = await Promise.allSettled(
        vars.file_ids.map((id) => api.post(`/files/${id}/restore`)),
      );
      const restored = results.filter((r) => r.status === 'fulfilled').length;
      const failed = results.length - restored;
      return { restored, failed };
    },
    onSuccess: (_d, vars) => qc.invalidateQueries({ queryKey: ['files', vars.folder_id] }),
  });
}

export function useBulkArchiveFiles() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (vars: { file_ids: string[]; folder_id: string; archive: boolean }) => {
      const { data } = await api.post<{ archived: number; failed: number }>('/files/bulk-archive', {
        file_ids: vars.file_ids,
        archive: vars.archive,
      });
      return data;
    },
    onSuccess: (_d, vars) => qc.invalidateQueries({ queryKey: ['files', vars.folder_id] }),
  });
}

export function useNewFileVersion() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (vars: { fileId: string; file: File }) => {
      const form = new FormData();
      form.append('file', vars.file);
      // Let the browser/axios set the multipart boundary automatically.
      const { data } = await api.post<FileVersion>(`/files/${vars.fileId}/versions`, form);
      return data;
    },
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({ queryKey: ['files', 'versions', vars.fileId] });
      qc.invalidateQueries({ queryKey: ['files', 'detail', vars.fileId] });
    },
  });
}
